import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { sessionResourceAtom } from 'atoms/root';
import { api } from 'lib/request/api';
import type { SingleLayerSpatialExportRequest } from 'components/export-spatial-data';
import { useServerExport, type ExportParams } from 'lib/hooks/useServerExport';
import {
  parseExportError,
  postSpatialExport,
  useSpatialExport,
} from 'lib/hooks/use-spatial-export';
import type { AreaGeometriesQueryParams } from './atoms';

// The Areas spatial export submits the shared single-layer request shape (formats + filters +
// attributes). Aliased, not redefined, so the Areas/Type Curves/single-layer surfaces can't drift.
export type AreasSpatialExportRequest = SingleLayerSpatialExportRequest;

/**
 * Owns the Areas page spatial (GIS) export: POSTs the selected formats + attributes + current filters
 * to the Areas spatial endpoint and tracks completion over Pusher (auto-downloading the bundle),
 * through the shared useSpatialExport flow. Mirrors useTypeCurveSpatialExport / useMapQuerySpatialExport.
 *
 * @return {object} The server-export status + `startExport(request)` + the signed-in user id.
 */
export const useAreasSpatialExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      postSpatialExport('queries/area_geometries/spatial_export', params),
    []
  );

  return useSpatialExport<AreasSpatialExportRequest>({
    event: 'area_geometries_spatial_export_complete',
    exportFn,
  });
};

export type AreasSpatialExport = ReturnType<typeof useAreasSpatialExport>;

/**
 * Owns the Areas page catalog (Excel) export: POSTs the current catalog filter params to the
 * area_geometries export endpoint and tracks completion over the signed-in user's Pusher channel
 * (auto-downloading the file), through the shared useServerExport flow. This restores prod's catalog
 * data export alongside the spatial (GIS) export; mirrors useTypeCurveListingExport. The current
 * filters ride in `startExport({ queryParams })`, scoping the file to exactly what the table shows.
 *
 * @return {object} The server-export status + `startExport(params)` + the signed-in user id.
 */
export const useAreasCatalogExport = () => {
  const userId = useAtomValue(sessionResourceAtom)?.id;

  const exportFn = useCallback(async (params?: ExportParams) => {
    const queryParams = (params?.queryParams ??
      {}) as AreaGeometriesQueryParams;
    try {
      return await api
        .post('area_geometries/export', { json: queryParams })
        .json<{ exportId: string }>();
    } catch (error) {
      // Surface the server's friendly message — the catalog export's query validation returns
      // attribute-keyed errors (e.g. { page_size: ["is not a number"] }), which parseExportError reads.
      throw await parseExportError(error);
    }
  }, []);

  const serverExport = useServerExport({
    // The completion channel is named after the signed-in user; `null` without a session so the hook
    // skips the subscription rather than binding `user_undefined_exports`.
    channel: userId ? `user_${userId}_exports` : null,
    event: 'area_geometries_export_complete',
    exportFn,
  });

  return { ...serverExport, userId };
};
